import { Box, Divider, Flex, Form, PageWrapper, Spacing, useLayout } from '@approvalmax/ui/src/components';
import { hocs as pageHocs } from 'modules/page/hocs';
import { memo } from 'react';
import { Helmet } from 'react-helmet';
import { useMount } from 'react-use';
import { amplitudeService } from 'services/amplitude';

import { pageConfig } from './config';
import Loading from './loading';
import {
    AdditionalServicesSection,
    IntegrationSection,
    OrganisationInfo,
    PopupDeleteOrganisation,
    PopupTransferImpossible,
    PopupTransferOrganisation,
    useOrganisationInfoForm,
} from './resources/components';
import { useBreadcrumbs } from './resources/hooks/useBreadcrumbs';
import { usePageData } from './resources/hooks/usePageData';
import { messages } from './resources/messages';
import { ContentProps } from './resources/types';

const Content = memo<ContentProps>((props) => {
    const { company } = props;

    useBreadcrumbs();

    const { form, onFormSubmit } = useOrganisationInfoForm(company);

    useMount(() => {
        amplitudeService.sendData('org settings: view org settings');
    });

    return (
        <PageWrapper>
            <Helmet title={messages.title({ companyName: company.displayName })} />

            <PageWrapper.Content spacingTop={0} spacingBottom={0}>
                <Form onSubmit={onFormSubmit} form={form} height='100%'>
                    <Flex wrap={false} height='100%' spacing='0'>
                        <Flex grow={1} block>
                            <Box spacing='0 40 0 0'>
                                <IntegrationSection company={company} />

                                <Spacing height={24} />

                                <AdditionalServicesSection company={company} />
                            </Box>
                        </Flex>

                        <Divider color='midnight50' orientation='vertical' />

                        <Flex grow={0} shrink={0} block>
                            <Box width={340} spacing='16 0 16 24'>
                                <OrganisationInfo company={company} />
                            </Box>
                        </Flex>
                    </Flex>
                </Form>

                <PopupDeleteOrganisation company={company} />

                <PopupTransferOrganisation company={company} />

                <PopupTransferImpossible />
            </PageWrapper.Content>
        </PageWrapper>
    );
});

export const Page = memo(() => {
    useLayout({ hasHeader: true, hasDrawer: true });

    const { isInitialLoading, company } = usePageData();

    return isInitialLoading || !company ? <Loading /> : <Content company={company} />;
});

export default pageHocs.withProtected({ Page, roles: pageConfig.access });
